import React, { useEffect, useState } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import {
  Row,
  Col,
  Button,
  DatePicker,
  InputNumber,
  Checkbox,
  message,
  Upload,
  ConfigProvider,
} from "antd";
import { UploadOutlined, EyeOutlined } from "@ant-design/icons";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import dayjs from "dayjs";
import { axiosPrivate } from "config/Api";
import { showNotification } from "service/Notification";
import MDBox from "components/MDBox";
import MDTypographyRoot from "components/MDTypography/MDTypographyRoot";
import MDTypography from "components/MDTypography";
import { approveAllowed, OTF_STATUS } from "./utils";
import TextArea from "antd/es/input/TextArea";
import { useAxiosPrivate } from "hooks/useAxiosPrivate";
import { json, useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, useMediaQuery, useTheme } from "@mui/material";
import { useMaterialUIController } from "context";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
// ================= YUP SCHEMA =================
// const schema = yup.object({
//   payments: yup
//     .array()
//     .of(
//       yup.object({
//         expectedDate: yup.string().required("Expected Date is required"),
//         percentage: yup
//           .number()
//           .typeError("Percentage required")
//           .required("Percentage required")
//           .min(1)
//           .max(100),
//         billingAllowed: yup.boolean(),
//       })
//     )
//     .min(1, "At least one payment required"),
//   bbaSigned: yup.boolean(),
//   bbaSignedDate: yup.string().when("bbaSigned", {
//     is: true,
//     then: (schema) => schema.required("BBA Date required"),
//     otherwise: (schema) => schema.nullable(),
//   }),
//   isRecordingNotPresent: yup.boolean(),
//   clientConfirmationRecording: yup
//     .object()
//     .shape({
//       message: yup.string().required("Recording required"),
//     })
//     .nullable()
//     .when("isRecordingNotPresent", {
//       is: false,
//       then: (schema) => schema.required("Recording required"),
//       otherwise: (schema) => schema.notRequired().nullable(),
//     }),
//   approvalForSkip: yup
//     .object()
//     .shape({
//       message: yup.string().required("Approval screenshot required"),
//     })
//     .nullable()
//     .when("isRecordingNotPresent", {
//       is: true,
//       then: (schema) => schema.required("Approval screenshot required"),
//       otherwise: (schema) => schema.notRequired().nullable(),
//     }),
// });

const schema = yup.object({
  action: yup.string().required("Action is required"), // APPROVE / REJECT / SAVE

  payments: yup.array().when("action", {
    is: (action) => action === "APPROVE" || action === "SAVE",
    then: (schema) =>
      schema
        .of(
          yup.object({
            expectedDate: yup.string().required("Expected Date is required"),
            percentage: yup
              .number()
              .typeError("Percentage required")
              .required("Percentage required")
              .min(1)
              .max(100),
            billingAllowed: yup.boolean(),
            actualDate: yup.string().nullable(),
          })
        )
        .min(1, "At least one payment required")
        // .test("one-billable-required", "Please select one Billable payment", (payments) =>
        //   payments?.some((p) => p.billingAllowed)
        // ),
        .test("one-billable-required", "Please select one Billable payment", function (payments) {
          if (!payments) return true;

          const hasBillable = payments.some((p) => p?.billingAllowed);

          if (!hasBillable) {
            return this.createError({
              path: `payments[0].billingAllowed`,
              message: "Please select one Billable payment",
            });
          }

          return true;
        }),
    otherwise: (schema) => schema.notRequired(),
  }),

  bbaSigned: yup.boolean().when("action", {
    is: "APPROVE",
    then: (schema) => schema.required("BBA Signed is required"),
  }),
  bbaSignedDate: yup
    .string()
    .nullable()
    .when(["bbaSigned", "action"], {
      is: (bbaSigned, action) => {
        console.log("bbasigned", bbaSigned);
        bbaSigned === true && action === "APPROVE";
      },
      then: (schema) => schema.required("BBA date required"),
    }),

  remark: yup.string().when("action", {
    is: "REJECT",
    then: (schema) => schema.required("Remark is required when rejecting"),
  }),

  isRecordingNotPresent: yup.boolean(),

  // APPROVE → recording required if present
  clientConfirmationRecording: yup.mixed().when(["action", "isRecordingNotPresent"], {
    is: (action, isRecordingNotPresent) => action === "APPROVE" && !isRecordingNotPresent,
    then: (schema) =>
      schema.test(
        "required-client-recording",
        "Recording required",
        (value) => value && value.message // must have message
      ),
    otherwise: (schema) => schema.notRequired(),
  }),

  // APPROVE → approval required if recording not present
  approvalForSkip: yup.mixed().when(["action", "isRecordingNotPresent"], {
    is: (action, isRecordingNotPresent) => action === "APPROVE" && isRecordingNotPresent,
    then: (schema) =>
      schema.test(
        "required-approval-skip",
        "Approval screenshot required",
        (value) => value && value.message // must have message
      ),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const UploadField = ({ name, fieldType, label, ocfDisplayId, control, disabled }) => {
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleView = async (fileName) => {
    try {
      setDownloading(true);
      const response = await axiosPrivate.get("/om/public/download", {
        params: { ocfDisplayId, documentId: fileName },
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: response.headers["content-type"] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      message.error("Unable to download file");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const existingFile = field.value?.message;
        const fileList = existingFile
          ? [{ uid: "-1", name: existingFile.split("_").slice(1).join("_"), status: "done" }]
          : [];
        return (
          <Upload
            maxCount={1}
            fileList={fileList}
            showUploadList={{ showRemoveIcon: true }}
            beforeUpload={async (file) => {
              if (field.value?.message) {
                message.warning("Delete existing file before uploading new one");
                return Upload.LIST_IGNORE;
              }

              try {
                setUploading(true);
                const fd = new FormData();
                fd.append("file", file);
                fd.append("data", JSON.stringify({ ocfDisplayId, fieldType, fileName: file.name }));

                const res = await axiosPrivate.post("/om/uploadFileForOcf", fd);

                // ✅ Always set value as object with `message` key
                field.onChange({ message: res.data.message });
              } catch (error) {
                message.error(error?.response?.data?.message || "Upload failed");
              } finally {
                setUploading(false);
              }

              return false;
            }}
            onRemove={async () => {
              try {
                setUploading(true);
                if (field.value?.message) {
                  await axiosPrivate.delete("/om/delete", {
                    params: { folder: ocfDisplayId, fileName: field.value.message },
                  });
                }
                field.onChange(null); // clear RHF value
              } catch (error) {
                message.error("Delete failed");
              } finally {
                setUploading(false);
              }
            }}
          >
            <Button
              icon={<UploadOutlined />}
              disabled={!!field.value?.message || uploading || disabled}
              loading={uploading}
            >
              {field.value?.message ? "Document Uploaded" : label}
            </Button>

            {field.value?.message && (
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() => handleView(field.value.message)}
              >
                View File
              </Button>
            )}
          </Upload>
        );
      }}
    />
  );
};

// ================= MAIN COMPONENT =================
const ReviewAndSubmit = ({ ocfId, ocf }) => {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    getValues,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      payments: [{ expectedDate: "", percentage: "", billingAllowed: false }],
      bbaSigned: false,
      bbaSignedDate: "",
      isRecordingNotPresent: false,
      clientConfirmationRecording: null,
      approvalForSkip: null,
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "payments" });
  const isBbaSigned = watch("bbaSigned");
  const bbaSignedDate = watch("bbaSignedDate");
  const isRecordingNotPresent = watch("isRecordingNotPresent");
  const navigate = useNavigate();

  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [openRejectdIALOG, setOpenRejecDialog] = useState(false);
  const handleApprove = handleSubmit(async (data) => {
    setOpenApproveDialog(true);
  });

  const handleSave = handleSubmit(async (data) => {
    callSaveApi();
  });

  const callSaveApi = async () => {
    const data = getValues();
    const output2 = {
      bbaSigned: data.bbaSigned,
      bbaSignedDate: data.bbaSignedDate,
      ocfId: ocfId,
      crmRemark: `${ocf?.crmRemark || ""}\n${data?.remark} ( AddedOn: ${dayjs().format(
        "DD-MM-YYYY HH:mm:ss"
      )})`,
      
    };
    const resp2 = await callApiStage2(output2);
    if (resp2.status == 200) {
      navigate(0);
    }
  };
  const callAprroveApi = async () => {
    const data = getValues();
    const output = {
      paymentDetails: data?.payments.map(
        ({ expectedDate, percentage, billingAllowed, actualDate }) => ({
          expectedDate,
          percentage,
          billingAllowed,
          actualDate,
        })
      ),
      ocfId: ocfId,
      approved: true,
      crmRemark: `${ocf?.crmRemark || ""}\n${data?.remark} ( AddedOn: ${dayjs().format(
        "DD-MM-YYYY HH:mm:ss"
      )})`,
    };

    const output2 = {
      bbaSigned: data.bbaSigned,
      bbaSignedDate: data.bbaSignedDate,
      ocfId: ocfId,
    };

    const resp = await callApiStage1(output);
    if ((resp.status = 200)) {
      navigate(0);
    }
    // if (data?.bbaSigned == false) {
    //   const resp = await callApiStage1(output);
    //   if ((resp.status = 200)) {
    //     navigate(0);n
    //   }
    // } else {
    //   await callApiStage1(output);
    //   const resp2 = await callApiStage2(output2);
    //   console.log(resp2);
    //   if (resp2.status == 200) {
    //     navigate(0);
    //   }
    // }
  };
  const handleReject = handleSubmit((data) => {
    setOpenRejecDialog(true);
  });

  const callRejectApi = async () => {
    const data = getValues();
    const output = {
      ocfId: ocfId,
      approved: false,
      crmRemark: `${ocf?.crmRemark || ""}\n${data?.remark} ( AddedOn: ${dayjs().format(
        "DD-MM-YYYY HH:mm:ss"
      )})`,
    };
    const resp = await callApiStage1(output);
    if (resp) {
      navigate(0);
    }
  };

  const isFormLocked = () => {
    return ["Submitted"].includes(ocf?.status);
  };

  const parsePaymentPlan = (plan) => {
    if (!plan) return [];
    if (plan.includes(":")) {
      return plan.split(":").map((p) => Number(p.trim()));
    }
    if (plan === "DP") return [];
    if (plan === "CLP") return [];
    return [];
  };

  useEffect(() => {
    if (!ocf) return;

    const payments = ocf.payment || []; // actual paid payments
    const paymentDetails = ocf.paymentDetails || []; // existing planned payments
    const paymentPlan = ocf.propertyResponse?.paymentPlan;
    const billingPercentage = Number(ocf.propertyResponse?.billingPercentage || 0);
    const defaultPaidPerPayment = 10;

    const parsePaymentPlan = (plan) => {
      if (!plan) return [];
      if (plan.includes(":")) return plan.split(":").map((p) => Number(p.trim()));
      if (plan === "CLP" || plan === "DP") return [0]; // special case → one empty row
      return [];
    };

    const planPercentages = parsePaymentPlan(paymentPlan);

    // 1️⃣ Existing payments (already paid)
    const paidRows = payments.map((p) => ({
      expectedDate: p.paymentDate || "",
      amount: p.amount || 0,
      billingAllowed: false, // already paid
      isPaid: true,
      confirmation: p.confirmation || true,
      type: p.type || "",
      percentage: defaultPaidPerPayment,
      actualDate: p?.paymentDate || null,
    }));
    // 2️⃣ Existing planned paymentDetails from API (user previously entered)
    const detailRows = paymentDetails.map((d) => ({
      expectedDate: d.expectedDate || "",
      amount: 0,
      percentage: d.percentage || 0,
      billingAllowed: !!d.billingAllowed,
      actualDate: d.actualDate || "",
      locked: true,
    }));

    // 3️⃣ Handle numeric plan or CLP/DP for future rows
    let futureRows = [];
    if ((planPercentages.length === 0 || planPercentages[0] === 0) && detailRows.length === 0) {
      // CLP/DP + no prior details → add empty row for user
      futureRows.push({
        expectedDate: "",
        amount: 0,
        percentage: 0,
        billingAllowed: true,
        locked: false,
      });
    } else if (planPercentages.length > 0 && detailRows.length === 0) {
      // Numeric plan + no prior details → generate remaining slabs
      let remainingPaid = payments.length * defaultPaidPerPayment;
      let billingSet = false;
      for (let slab of planPercentages) {
        let remainingPercentage = slab;
        if (remainingPaid > 0) {
          if (remainingPaid >= slab) {
            remainingPercentage = 0;
            remainingPaid -= slab;
          } else {
            remainingPercentage = slab - remainingPaid;
            remainingPaid = 0;
          }
        }

        let allowed = false;
        if (
          !billingSet &&
          remainingPercentage + payments.length * defaultPaidPerPayment >= billingPercentage
        ) {
          allowed = true;
          billingSet = true;
        }

        futureRows.push({
          expectedDate: "",
          amount: 0,
          percentage: remainingPercentage,
          billingAllowed: allowed,
          locked: false,
        });
      }
    }

    // 4️⃣ Combine rows: paid + API paymentDetails + future
    // const defaultPayments = [...paidRows, ...detailRows, ...futureRows];
    let defaultPayments;
    if (
      ocf?.status == OTF_STATUS.APPROVED ||
      ocf?.status == OTF_STATUS.BBA_Confirmed ||
      ocf?.status == OTF_STATUS.BILLED ||
      ocf?.status == OTF_STATUS.COLLECTED
      //  ||
      // ocf?.status == "Submitted"
    ) {
      defaultPayments = [...detailRows, ...futureRows];
    } else {
      defaultPayments = [...paidRows, ...detailRows, ...futureRows];
    }

    // 5️⃣ Set form values
    setValue("payments", defaultPayments, { shouldValidate: true, shouldDirty: true });

    // Also populate BBA fields
    setValue("bbaSigned", !!ocf.bbaSigned);
    console.log("12345678", ocf.bbaSignedDate || null);
    setValue("bbaSignedDate", ocf.bbaSignedDate || null);
    setValue(
      "approvalForSkip",
      ocf?.documentIds?.approvalForSkip ? { message: ocf.documentIds.approvalForSkip } : null
    );
    setValue(
      "clientConfirmationRecording",
      ocf?.documentIds?.clientConfirmationRecording
        ? { message: ocf.documentIds.clientConfirmationRecording }
        : null
    );
  }, [ocf, setValue]);

  // const handleApprove = async (data) => {
  //   console.log("approve clicked");
  //   console.log("data", data);
  //   const valid = await trigger();
  //   if (!valid) {
  //     return; // errors will automatically show
  //   }
  //   const output = {
  //     paymentDetails: data.futurePaymentsForm.payments.map(
  //       ({ expectedDate, percentage, billingAllowed }) => ({
  //         expectedDate,
  //         percentage,
  //         billingAllowed,
  //       })
  //     ),
  //     ocfId: ocfId,
  //     approved: true,
  //   };

  //   const output2 = {
  //     bbaSigned: data.futurePaymentsForm.bbaSigned,
  //     bbaSignedDate: data.futurePaymentsForm.bbaSignedDate,
  //     ocfId: ocfId,
  //   };
  //   console.log(data?.futurePaymentsForm?.bbaSigned == false);
  //   if (data?.bbaSigned == false) {
  //     const resp = await callApiStage1(output);
  //     if ((resp.status = 200)) {
  //       navigate(0);
  //     }
  //   } else {
  //     await callApiStage1(output);
  //     const resp2 = await callApiStage2(output2);
  //     console.log(resp2);
  //     if (resp2.status == 200) {
  //       navigate(0);
  //     }
  //   }
  // };

  //   console.log(errors);

  //   const handleReject = (data) => {
  //     console.log("Rejected:", data.futurePaymentsForm);
  //     const output = {
  //       paymentDetails: data.futurePaymentsForm.payments.map(
  //         ({ expectedDate, percentage, billingAllowed }) => ({
  //           expectedDate,
  //           percentage,
  //           billingAllowed,
  //         })
  //       ),
  //       ocfId: ocfId,
  //       approved: false,
  //     };
  //     callApiStage1(output);
  //   };
  //   const handleRejectAction = () => {
  //     setValue("futurePaymentsForm.action", "REJECT", {
  //       shouldValidate: true,
  //       shouldDirty: true,
  //     });
  //     handleSubmit(handleReject)();
  //   };

  const axiosPrivate = useAxiosPrivate();

  const callApiStage1 = async (data) => {
    return await axiosPrivate({
      method: "POST",
      url: "om/payment?stage=1",
      data: data,
    });
  };

  const callApiStage2 = async (data) => {
    return await axiosPrivate({
      method: "POST",
      url: "om/payment?stage=2",
      data: data,
    });
  };
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const isFormDisabled = (status) => {
    return ["Confirmed", "Completed", "Billed", "Collected"].includes(status);
  };

  console.log(errors);
  // const colProps = {
  //   xs: 12,
  //   sm: 8,
  //   md: 5,
  //   lg: 4,
  // };
  const colProps = { xs: 24, sm: 12, md: 6, lg: 6 };
  return (
    <>
      <MDBox
        style={{
          display: "flex",
          alignItems: "center",
          gap: "24px",
          margin: "12px",
        }}
      >
        <MDBox>
          Payment Plan: <strong>{ocf?.propertyResponse?.paymentPlan}</strong>
        </MDBox>

        <MDBox>
          Billing Percentage: <strong>{ocf?.propertyResponse?.billingPercentage}%</strong>
        </MDBox>
      </MDBox>
      <ConfigProvider componentDisabled={isFormDisabled(ocf?.status)}>
        {fields.map((item, index) => (
          <Row
            key={item.id}
            gutter={16}
            style={{ marginTop: 20, display: "flex", alignItems: "center" }}
          >
            <Col {...colProps}>
              <label>{item?.confirmation ? "Payment Date " : " Excepted Date"}</label>
              <Controller
                name={`payments.${index}.expectedDate`}
                control={control}
                render={({ field }) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value, "DD-MM-YYYY") : null}
                    onChange={(d) => field.onChange(d ? d.format("DD-MM-YYYY") : null)}
                    style={{ width: "100%" }}
                    format="DD-MM-YYYY"
                    disabled={item?.confirmation || item?.locked}
                  />
                )}
              />
              <p style={{ color: "red" }}>{errors?.payments?.[index]?.expectedDate?.message}</p>
            </Col>
            <Col {...colProps}>
              <label>Billing %</label>
              <Controller
                name={`payments.${index}.percentage`}
                control={control}
                render={({ field }) => (
                  <InputNumber
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    min={1}
                    max={100}
                    style={{ width: "100%" }}
                    disabled={item?.locked}
                  />
                )}
              />
              <p style={{ color: "red" }}>{errors?.payments?.[index]?.percentage?.message}</p>
            </Col>
            <Col {...colProps}>
              <Controller
                name={`payments.${index}.billingAllowed`}
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onChange={(e) => {
                      const checked = e.target.checked;

                      // 1️⃣ Update current checkbox
                      field.onChange(checked);

                      if (checked) {
                        // 2️⃣ Deselect all other rows
                        fields.forEach((f, i) => {
                          if (i !== index) {
                            setValue(`payments.${i}.billingAllowed`, false, {
                              shouldValidate: true,
                            });
                          }
                        });
                      }
                    }}
                    disabled={item?.locked}
                  >
                    Billable
                  </Checkbox>
                )}
              />
              <p style={{ color: "red", fontSize: 12 }}>
                {errors?.payments?.[index]?.billingAllowed?.message}
              </p>
            </Col>
            <Col {...colProps}>
              {/* {!item?.confirmation ||
                (item?.locked && (
                  <Button danger onClick={() => remove(index)}>
                    Remove
                  </Button>
                ))} */}
              {}
              <Button
                danger
                onClick={() => remove(index)}
                disabled={item?.confirmation || item?.locked}
              >
                Remove
              </Button>
            </Col>
          </Row>
        ))}
        <Button
          type="secondary"
          onClick={() => append({ expectedDate: "", percentage: "", billingAllowed: false })}
        >
          Add Expected Payment
        </Button>
        <Row style={{ marginTop: 20 }}>
          <Col {...colProps}>
            <UploadField
              name="clientConfirmationRecording"
              fieldType="clientConfirmationRecording"
              label="Client Confirmation Recording"
              ocfDisplayId={ocf?.ocfDisplayId}
              control={control} // <-- ADD THIS
              disabled={watch("isRecordingNotPresent") || isFormDisabled(ocf?.status)}
            />

            <p style={{ color: "red" }}>{errors?.clientConfirmationRecording?.message}</p>
          </Col>

          <Col {...colProps}>
            <Controller
              name="isRecordingNotPresent"
              control={control}
              render={({ field }) => (
                <Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)}>
                  Recording Not Available
                </Checkbox>
              )}
            />
          </Col>

          {watch("isRecordingNotPresent") && (
            <Col {...colProps}>
              <UploadField
                name="approvalForSkip"
                fieldType="approvalForSkip"
                label="Approval For Skip"
                ocfDisplayId={ocf?.ocfDisplayId}
                control={control}
                disabled={isFormDisabled(ocf?.status)}
              />
              <p style={{ color: "red" }}>{errors?.approvalForSkip?.message}</p>
            </Col>
          )}
        </Row>
        <Row>
          {ocf?.crmRemark && (
            <label style={{ whiteSpace: "pre-line", marginBottom: "12px" }}>{ocf?.crmRemark}</label>
          )}
        </Row>
        <Row gutter={[16, 16]}>
          <Col {...colProps}>
            <label>Remark</label>

            <Controller
              name="remark"
              control={control}
              render={({ field }) => (
                <>
                  <TextArea {...field} rows={3} />

                  {/* ERROR MESSAGE */}
                  {errors?.remark && (
                    <p style={{ color: "#ff4d4f", fontSize: "12px", marginTop: 4 }}>
                      {errors.remark.message}
                    </p>
                  )}
                </>
              )}
            />
          </Col>
        </Row>
        {OTF_STATUS.APPROVED == ocf?.status && (
          <Row style={{ marginTop: 20 }}>
            <Col {...colProps}>
              <Controller
                name="bbaSigned"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  >
                    BBA Signed
                  </Checkbox>
                )}
              />
            </Col>
            <Col {...colProps}>
              {/* <Controller
                name="bbaSignedDate"
                control={control}
                rules={{ required: isBbaSigned ? "BBA Date required" : false }}
                render={({ field }) => (
                  <>
                    <p>{field?.value}</p>
                    <DatePicker
                      // value={field.value ? dayjs(field.value) : null}
                      // onChange={(d) => field.onChange(d ? d.format("DD-MM-YYYY") : "")}
                      // format={"DD-MM-YYYY"}
                      format="DD-MM-YYYY"
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(date) => field.onChange(date ? date.format("YYYY-MM-DD") : null)}
                    />
                  </>
                )}
              /> */}
              <Controller
                name="bbaSignedDate"
                control={control}
                rules={{ required: isBbaSigned ? "BBA Date required" : false }}
                render={({ field, fieldState }) => (
                  <>
                    <DatePicker
                      format="YYYY-MM-DD"
                      value={field.value}
                      onChange={(date) => field.onChange(date)}
                    />

                    {fieldState.error && <p style={{ color: "red" }}>{fieldState.error.message}</p>}
                  </>
                )}
              />
              <p style={{ color: "red" }}>{errors?.bbaSignedDate?.message}</p>
            </Col>
          </Row>
        )}
        <Row style={{ marginTop: 30 }} gutter={16}>
          {approveAllowed(ocf?.status) && (
            <>
              <Col {...colProps}>
                <Button
                  type="primary"
                  onClick={() => {
                    setValue("action", "APPROVE", { shouldValidate: true, shouldDirty: true });
                    handleApprove();
                  }}
                >
                  Approve
                </Button>
              </Col>
              <Col {...colProps}>
                <Button
                  danger
                  onClick={() => {
                    setValue("action", "REJECT", { shouldValidate: true, shouldDirty: true });
                    handleReject();
                  }}
                >
                  Reject
                </Button>
              </Col>
            </>
          )}
        </Row>
        <Row style={{ marginTop: 30 }} gutter={16}>
          <Col {...colProps}>
            {/* <Button type="primary" onClick={handleSubmit(onSubmit)}>
            Save
          </Button> */}
            {ocf?.status == "Approved" && (
              <Button
                type="primary"
                onClick={() => {
                  setValue("action", "SAVE", { shouldValidate: false, shouldDirty: true });
                  // handleSubmit(onSubmit)();
                  handleSave();
                }}
                disabled={!(isBbaSigned && bbaSignedDate)}
              >
                Save
              </Button>
            )}
          </Col>
        </Row>
      </ConfigProvider>

      <Dialog
        open={openApproveDialog}
        onClose={() => setOpenApproveDialog(false)}
        fullScreen={fullScreen}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: darkMode ? "#1a2035" : "#fff", // same as ConfigProvider example
            color: darkMode ? "#fff" : "#000",
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle>Confirm Submission </DialogTitle>
        <DialogContent>
          <MDTypography>Are you sure to Approve this OCF?</MDTypography>
          <MDBox mt={2} display="flex" justifyContent="flex-end" gap={2}>
            <Button onClick={() => setOpenApproveDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setOpenApproveDialog(false);
                callAprroveApi();
              }}
              // loading={loading}
            >
              Confirm
            </Button>
          </MDBox>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openRejectdIALOG}
        onClose={() => setOpenRejecDialog(false)}
        fullScreen={fullScreen}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: darkMode ? "#1a2035" : "#fff", // same as ConfigProvider example
            color: darkMode ? "#fff" : "#000",
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle>Confirm Submission </DialogTitle>
        <DialogContent>
          <MDTypography>Are you sure to Reject this OCF?</MDTypography>
          <MDBox mt={2} display="flex" justifyContent="flex-end" gap={2}>
            <Button onClick={() => setOpenRejecDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setOpenRejecDialog(false);
                callRejectApi();
              }}
              // loading={loading}
            >
              Confirm
            </Button>
          </MDBox>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReviewAndSubmit;
